import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import { FaChevronDown, FaUser, FaPen, FaRandom, FaUserPlus, FaSignInAlt } from "react-icons/fa";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useUser } from "@/redux/user";
import { useGamePlay } from "@/redux/gamePlay";
import { changeGuestName } from "@/services/guestName";
import { availableGuestName } from "@/util/guestNames";
import { GuestIdentity } from "@/util/guestIdentity";
import { hasBadWord } from "@/util/badWords";
import { trackEvent } from "@/services/eventTracker";
import styles from "./GuestMenu.module.css";

export default function GuestMenu({ compact = false }: { compact?: boolean }) {
  const { t, lang } = useTranslation("common");
  const { displayName, memberLoggedIn, persistentGuest } = useUser();
  const { gameEnded } = useGamePlay();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [generated, setGenerated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState<GuestIdentity | null>(null);
  const [status, setStatus] = useState("");
  const dialog = useRef<HTMLDialogElement>(null);
  const applying = useRef(false);

  useEffect(() => {
    if (editing && dialog.current && !dialog.current.open) dialog.current.showModal();
  }, [editing]);

  useEffect(() => {
    if (!pending || !gameEnded || memberLoggedIn || applying.current) return;
    applying.current = true;
    void changeGuestName(pending).then((failure) => {
      setPending(null);
      setStatus(failure ? t(`guestIdentity.error.${failure}`) : t("guestIdentity.changed", { name: pending.name }));
    }).finally(() => { applying.current = false; });
  }, [pending, gameEnded, memberLoggedIn, t]);

  function openEditor() {
    setName(pending?.name || displayName || "");
    setGenerated(false);
    setError("");
    setEditing(true);
  }

  async function generate() {
    setBusy(true);
    setError("");
    try {
      setName(await availableGuestName([name, displayName]));
      setGenerated(true);
    } finally { setBusy(false); }
  }

  async function save() {
    const trimmed = name.trim();
    if (!trimmed || trimmed.length > 18 || /[\u0000-\u001f\u007f]/.test(trimmed) || hasBadWord(trimmed)) {
      setError(t("guestIdentity.error.invalid"));
      return;
    }
    const identity = { name: trimmed, generated, chosen: true };
    if (!gameEnded) {
      setPending(identity);
      setStatus(t("guestIdentity.pending", { name: trimmed }));
      setEditing(false);
      return;
    }
    setBusy(true);
    const failure = await changeGuestName(identity);
    setBusy(false);
    if (failure) setError(t(`guestIdentity.error.${failure}`));
    else {
      setPending(null);
      setEditing(false);
      setStatus(t("guestIdentity.changed", { name: trimmed }));
      void trackEvent("guest_name_changed", { entrySource: "navigation", generated });
    }
  }

  return <>
    <DropdownMenu dir={["ar", "he", "fa", "ps"].includes(lang) ? "rtl" : "ltr"}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={`${compact ? styles.compactTrigger : "max-w-[190px]"} flex min-h-[44px] items-center gap-1 rounded-lg px-1 text-slate-700 hover:bg-slate-100 sm:gap-2 sm:px-2 focus-visible:ring-2 focus-visible:ring-primary`} aria-label={t("guestIdentity.menu", { name: displayName || t("guestIdentity.guest") })}>
          <FaUser className="hidden shrink-0 text-slate-400 lg:block" aria-hidden="true" />
          <span className={compact ? styles.compactIdentity : "min-w-0 text-start"}>
            <span className={`${styles.name} block truncate text-sm font-semibold`}><bdi>{displayName || t("guestIdentity.guest")}</bdi></span>
            {compact && <span className="shrink-0 text-xs text-slate-400" aria-hidden="true">·</span>}
            <span className="block shrink-0 text-xs text-slate-500">{t("guestIdentity.guest")}</span>
          </span>
          <FaChevronDown className="shrink-0 text-[10px]" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`${styles.content} rounded-xl p-2 shadow-lg`}>
        <DropdownMenuItem className="min-h-[44px] gap-3 px-3" onSelect={openEditor}><FaPen className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />{t("guestIdentity.editName")}</DropdownMenuItem>
        <DropdownMenuItem className="min-h-[44px] gap-3 px-3" onSelect={() => { openEditor(); void generate(); }}><FaRandom className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />{t("guestIdentity.randomName")}</DropdownMenuItem>
        {pending && <div className="px-3 py-2 text-xs text-slate-500" role="status">{t("guestIdentity.pending", { name: pending.name })}</div>}
        <DropdownMenuItem asChild className={`${styles.accountAction} my-2 min-h-[56px] gap-3 rounded-lg px-3 py-3`}>
          <Link href="/register" onClick={() => { void trackEvent("guest_name_keep_selected", { entrySource: "navigation" }); }}>
            <FaUserPlus className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 text-start"><span className="block font-semibold">{t("Sign up")}</span><span className="block text-xs font-normal">{t("guestIdentity.keep")}</span></span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="min-h-[44px] gap-3 px-3"><Link href="/signIn"><FaSignInAlt className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />{t("Log in")}</Link></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <span className="sr-only" role="status">{status}</span>
    {editing && <dialog ref={dialog} onCancel={() => setEditing(false)} className="w-[calc(100%_-_24px)] max-w-md rounded-xl p-6 text-slate-800 shadow-xl backdrop:bg-black/40" aria-labelledby="guest-name-title">
      <form onSubmit={(event) => { event.preventDefault(); void save(); }}>
        <h2 id="guest-name-title" className="text-xl font-bold">{t("guestIdentity.change")}</h2>
        <p className="mt-2 text-sm text-slate-500">{t(persistentGuest ? "guestIdentity.savedGuestProgress" : "guestIdentity.remembered")}</p>
        <label htmlFor="guest-name-editor" className="mb-2 mt-5 block text-sm font-semibold">{t("guestIdentity.name")}</label>
        <input id="guest-name-editor" autoFocus value={name} maxLength={18} onChange={(event) => { setName(event.target.value); setGenerated(false); setError(""); }} className="min-h-[48px] w-full rounded-lg border border-slate-300 px-3 text-base" aria-invalid={!!error} aria-describedby={error ? "guest-name-error" : undefined} disabled={busy} />
        {error && <p id="guest-name-error" className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
        {!gameEnded && <p className="mt-2 text-sm text-slate-500">{t("guestIdentity.afterGame")}</p>}
        <button type="button" disabled={busy} onClick={() => { void generate(); }} className="mt-2 min-h-[44px] text-sm text-altblue-600 hover:underline disabled:opacity-50">{t("guestIdentity.generate")}</button>
        <div className="mt-5 flex justify-end gap-3">
          <button type="button" disabled={busy} onClick={() => setEditing(false)} className="min-h-[44px] rounded-lg px-4 text-sm hover:bg-slate-100">{t("guestIdentity.cancel")}</button>
          <button type="submit" disabled={busy} className="min-h-[44px] rounded-lg bg-altblue-600 px-5 font-semibold text-white disabled:opacity-50">{busy ? t("guestIdentity.saving") : t("guestIdentity.save")}</button>
        </div>
      </form>
    </dialog>}
  </>;
}
